// Per-file spec card: Copies · Paper · Colour · Binding · Sides · Finishing · Covers · Notes.
// Rendered inside <UploadedFileRow> for each uploaded file.

import { FileText, Copy } from "lucide-react";
import { ksh } from "@/lib/api";
import { ORDER } from "@/constants/testIds";
import {
  SegBtn,
  ColorPagesPanel,
  PrintPagesSelector,
  CoverPicker,
  EngravingUploader,
} from "./_shared";
import {
  PAPER_SIZES,
  COLOR_OPTIONS,
  BINDING_OPTIONS,
  COVER_PALETTE,
} from "./pricing";
import { FinishingSection } from "./FinishingSection";

export function SpecCard({ idx, item, pricing, itemTotal, onUpdate }) {
  const { file, spec } = item;
  const isPdf = ((file.file_name || file.original_name || "").toLowerCase().endsWith(".pdf"));

  return (
    <>
      <div className="grid sm:grid-cols-2 gap-4">
        {/* Copies */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-2">Copies</div>
          <div className="flex items-center border border-[#E5E5E5] w-fit">
            <button onClick={() => onUpdate({ copies: Math.max(1, spec.copies - 1) })} className="w-10 h-10 hover:bg-[#0A0A0A] hover:text-white">−</button>
            <input
              data-testid={`${ORDER.specCopies}-${idx}`}
              type="number"
              min={1}
              value={spec.copies}
              onChange={(e) => onUpdate({ copies: Math.max(1, parseInt(e.target.value || "1")) })}
              className="w-16 h-10 text-center mono font-bold border-l border-r border-[#E5E5E5] outline-none"
            />
            <button onClick={() => onUpdate({ copies: spec.copies + 1 })} className="w-10 h-10 hover:bg-[#0A0A0A] hover:text-white">+</button>
          </div>
          {/* Print all pages toggle — sits under Copies so it reads
              left-to-right as "how many copies of what". */}
          <PrintPagesSelector
            idx={idx}
            spec={spec}
            totalPages={file.pages}
            onChange={onUpdate}
          />
        </div>

        {/* Paper Size */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-2">Paper Size</div>
          <div className="flex flex-wrap gap-1">
            {PAPER_SIZES.map((p) => (
              <SegBtn key={p} active={spec.paper_size === p} onClick={() => onUpdate({ paper_size: p })} testId={`${ORDER.specPaper}-${p}-${idx}`} icon={FileText}>
                {p}
              </SegBtn>
            ))}
          </div>
        </div>

        {/* Color */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-2">Color Mode</div>
          <div className="flex flex-wrap gap-1">
            {COLOR_OPTIONS.map((c) => {
              const disabled = c.value === "auto" && !isPdf;
              return (
                <SegBtn
                  key={c.value}
                  active={spec.color_mode === c.value}
                  onClick={() => !disabled && onUpdate({ color_mode: c.value })}
                  disabled={disabled}
                  testId={`${ORDER.specColor}-${c.value}-${idx}`}
                  icon={c.icon}
                >
                  {c.label}
                </SegBtn>
              );
            })}
          </div>
          {(spec.color_mode === "auto" || spec.color_mode === "custom") && (
            <ColorPagesPanel
              idx={idx}
              file={file}
              spec={spec}
              pricing={pricing}
              onChange={onUpdate}
            />
          )}
        </div>

        {/* Binding */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-2">
            Binding {(spec.lamination || spec.stapling) && <span className="text-[#525252] normal-case tracking-normal">— disabled while {spec.lamination ? "Lamination" : "Stapling"} is on</span>}
          </div>
          <div className="flex flex-wrap gap-1">
            {BINDING_OPTIONS.map((b) => {
              const disabled = (spec.lamination || spec.stapling) && b.value !== "none";
              return (
                <SegBtn
                  key={b.value}
                  active={spec.binding === b.value}
                  onClick={() => !disabled && onUpdate({ binding: b.value })}
                  testId={`${ORDER.specBinding}-${b.value}-${idx}`}
                  disabled={disabled}
                  icon={b.icon}
                >
                  {b.label}
                </SegBtn>
              );
            })}
          </div>
        </div>

        {/* Sided — segmented control with both options visible */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-2">Print Sides</div>
          <div className="flex flex-wrap gap-1">
            <SegBtn
              active={!spec.double_sided}
              onClick={() => onUpdate({ double_sided: false })}
              testId={`${ORDER.specSided}-single-${idx}`}
              icon={FileText}
            >
              Single-sided
            </SegBtn>
            <SegBtn
              active={spec.double_sided}
              onClick={() => onUpdate({ double_sided: true })}
              testId={`${ORDER.specSided}-double-${idx}`}
              icon={Copy}
            >
              Double-sided
            </SegBtn>
          </div>
        </div>

        {/* Finishing toggles: stapling · lamination · batch */}
        <FinishingSection idx={idx} spec={spec} pricing={pricing} onUpdate={onUpdate} />

        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-2">Item Cost</div>
          <div className="mono font-display font-black text-2xl text-[#0A0A0A]">{ksh(itemTotal)}</div>
        </div>
      </div>

      {/* Cover selectors — depends on binding type. Spiral/tape get separate
          top + back pickers; hardcover uses a single solid cover applied to
          both faces. */}
      {spec.binding !== "none" && (
        <div className="mt-4 border-t border-[#E5E5E5] pt-4 space-y-4">
          <CoverPicker
            testIdPrefix={`cover-top-${idx}`}
            label={spec.binding === "hardcover" ? "Cover Colour" : "Top Cover"}
            hint={spec.binding === "hardcover" ? "Applied to front & back of the hardback." : "The front-facing cover."}
            colors={COVER_PALETTE[spec.binding]?.top || []}
            value={spec.binding_top_cover}
            onPick={(c) => onUpdate({ binding_top_cover: c })}
          />
          {spec.binding !== "hardcover" && (
            <CoverPicker
              testIdPrefix={`cover-back-${idx}`}
              label="Back Cover"
              hint="Sits at the back of the book."
              colors={COVER_PALETTE[spec.binding]?.back || []}
              value={spec.binding_back_cover}
              onPick={(c) => onUpdate({ binding_back_cover: c })}
            />
          )}
          {/* Gold engraving on the top cover — hardcover only. */}
          {spec.binding === "hardcover" && (
            <div className="border-t border-[#E5E5E5] pt-4">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!spec.binding_gold_engraving}
                  onChange={(e) => onUpdate({ binding_gold_engraving: e.target.checked })}
                  data-testid={`engraving-check-${idx}`}
                  className="mt-0.5 w-4 h-4 accent-[#059669]"
                />
                <div>
                  <div className="text-sm font-bold text-[#0A0A0A]">Gold engraving on top cover</div>
                  <div className="text-[11px] text-[#525252] mt-0.5">
                    Personalise the front cover with a gold-foil title (name, book title, date, etc.).
                  </div>
                </div>
              </label>
              {spec.binding_gold_engraving && (
                <div className="mt-3 pl-7 space-y-3" data-testid={`engraving-panel-${idx}`}>
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252]">Engraving source</div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { key: "cover_page", label: "Use my cover-page details" },
                      { key: "upload", label: "Upload separate artwork" },
                    ].map((opt) => {
                      const active = spec.binding_engraving_source === opt.key;
                      return (
                        <button
                          key={opt.key}
                          onClick={() => onUpdate({ binding_engraving_source: opt.key })}
                          data-testid={`engraving-source-${opt.key}-${idx}`}
                          className={`px-3 py-2 border text-[11px] uppercase tracking-[0.15em] font-bold ${
                            active ? "border-[#0A0A0A] bg-[#0A0A0A] text-white" : "border-[#E5E5E5] hover:border-[#0A0A0A]"
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  {spec.binding_engraving_source === "upload" && (
                    <EngravingUploader
                      idx={idx}
                      current={{ id: spec.binding_engraving_file_id, name: spec.binding_engraving_file_name }}
                      onUploaded={(f) => onUpdate({
                        binding_engraving_file_id: f.id,
                        binding_engraving_file_name: f.original_name,
                      })}
                      onClear={() => onUpdate({
                        binding_engraving_file_id: null,
                        binding_engraving_file_name: null,
                      })}
                    />
                  )}
                  {spec.binding_engraving_source === "cover_page" && (
                    <div className="text-[11px] text-[#525252] bg-[#059669]/5 border border-[#059669]/30 px-3 py-2">
                      We&apos;ll use the first page of <span className="mono font-bold">{spec.file_name}</span> as the engraving reference. Make sure it&apos;s legible.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="mt-4">
        <input
          type="text"
          placeholder="Notes for this file (optional)"
          value={spec.notes || ""}
          onChange={(e) => onUpdate({ notes: e.target.value })}
          data-testid={`${ORDER.specNotes}-${idx}`}
          className="w-full px-3 py-2 border border-[#E5E5E5] text-sm focus:border-[#0A0A0A] outline-none"
        />
      </div>
    </>
  );
}
